import { ALL_PERKS_UNLOCKED } from '../data/shardUnlocks.js';
import { MAX_COPY_LEVEL } from '../data/pets.js';
import { CHARACTERS } from '../data/characters.js';

import { satisfiedAchievements } from '../data/achievements.js';

const STORAGE_KEY = 'survivor:save';
const SCHEMA_VERSION = 9;
export const ESSENCE_RARITIES = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'mythical', 'rainbow'];
export const ESSENCE_FUSION_COST = 3;
export const ESSENCE_PET_COST = 2;

// See _migrate(). Keyed by the version being migrated FROM (e.g. a future
// `MIGRATIONS[1] = (old) => ({ ...old, ... })` migrates v1 -> v2).
const MIGRATIONS = {
  2: (old) => ({ ...old, essence: old.essence ?? {} }),
  // Pets became equippable: ownedPets moves from a flat presence-only array
  // to a petId -> copy level map. There's no way to reconstruct past
  // duplicate counts (old dupes were already spent as essence with no
  // record kept), so every currently-owned pet starts fresh at level 1.
  3: (old) => ({
    ...old,
    ownedPets: Array.isArray(old.ownedPets)
      ? Object.fromEntries(old.ownedPets.map((id) => [id, 1]))
      : (old.ownedPets ?? {}),
    equippedPets: old.equippedPets ?? [],
  }),
  // The Awakening perk was folded into the champion ultimate (one meter, one
  // button), so its unlock no longer exists. Drop the dead id and refund what
  // it cost, rather than silently pocketing 30 Shards from anyone who bought
  // it — the shop iterates SHARD_UNLOCKS, so the id would otherwise just sit
  // in the save doing nothing.
  4: (old) => {
    const perks = Array.isArray(old.unlockedPerks) ? old.unlockedPerks : [];
    if (!perks.includes('transformation')) return { ...old };
    return {
      ...old,
      unlockedPerks: perks.filter((id) => id !== 'transformation'),
      shards: (old.shards ?? 0) + AWAKENING_REFUND,
    };
  },
  // The first-run tutorial arrived long after launch. Anyone with a run
  // already behind them has demonstrably learned to move, dash, and shoot —
  // grandfather them past it rather than opening a returning player's next
  // session with a lesson in WASD.
  5: (old) => ({
    ...old,
    tutorialSeen: (old.statistics?.runs ?? 0) > 0,
  }),
  // `clearedActs` arrived with the act split (data/acts.js). Act unlocks used
  // to be gated on `defeatedBosses`, which records a kill in ANY mode — so a
  // single Boss Challenge win against the Unmade King opened Act II. The gate
  // is an act CLEAR now, and nothing on an old save records one.
  //
  // Seed it from the kills those saves do have, so a returning player is not
  // re-locked out of acts they earned. That grandfathers in anyone who used
  // the Boss Challenge route before it closed, which is the right trade: it
  // is one-time and backward-looking, and the alternative is taking an act
  // away from players who cleared it honestly.
  6: (old) => {
    const killed = old.statistics?.defeatedBosses ?? [];
    const cleared = [];
    if (killed.includes('unmadeKing')) cleared.push('act1');
    if (killed.includes('undersong')) cleared.push('act2');
    if (killed.includes('anthem')) cleared.push('act3');
    // Rebuilt on top of the default statistics rather than spread straight onto
    // `old.statistics`: _migrate's merge is shallow, so whatever this returns
    // REPLACES the defaults wholesale — and a save missing the key entirely
    // would otherwise come back with `clearedActs` and nothing else, leaving
    // every counter undefined for the next `stats.runs++` to turn into NaN.
    return { ...old, statistics: { ...defaultSave().statistics, ...old.statistics, clearedActs: cleared } };
  },
  // `reduceEffects` was one switch over two unrelated disabilities: camera
  // shake and time-feel (vestibular / motion sickness) and screen flashes
  // (photosensitivity). A player who cannot take strobing had to also give up
  // hit-stop, and vice versa. Split into `reduceShake` + `reduceFlash`, both
  // seeded from the old value so nobody's existing setup changes.
  //
  // `skipAnimations` is NOT seeded from it. Presentational motion — the chest's
  // arc, the mote bob, the reel stagger — was bundled into the same switch,
  // which meant the safety default (reduceEffects defaults ON) was silently
  // stripping juice from every player who never opened the menu. It starts
  // off for everyone and is opted into on its own terms.
  7: (old) => {
    const reduced = old.settings?.reduceEffects ?? true;
    return {
      ...old,
      settings: {
        ...defaultSave().settings,
        ...old.settings,
        reduceShake: reduced,
        reduceFlash: reduced,
        skipAnimations: false,
      },
    };
  },
  // The champion Shard ladder was switched on (data/characters.js): five
  // champions now ship `locked: true`, and `isCharacterUnlocked` stopped
  // returning an unconditional `true`.
  //
  // Anyone already playing had all ten, and taking five of them back —
  // including whichever one they main — would be a straight confiscation for
  // a change that is about giving *new* players something to earn. So a save
  // that has finished a run keeps everything. Same trade, and the same
  // reasoning, as MIGRATIONS[6] grandfathering act clears: one-time,
  // backward-looking, and the alternative punishes people for having played
  // earlier.
  //
  // A save with zero runs is one that never got anywhere; it starts on the
  // ladder like a new one.
  8: (old) => {
    if ((old.statistics?.runs ?? 0) === 0) return old;
    const owned = new Set(old.unlockedCharacters ?? []);
    for (const character of CHARACTERS) owned.add(character.id);
    return { ...old, unlockedCharacters: [...owned] };
  },
};

// What the removed Awakening unlock cost, kept as a literal since its
// SHARD_UNLOCKS entry is gone (see MIGRATIONS[4]).
const AWAKENING_REFUND = 30;

function defaultSave() {
  return {
    version: SCHEMA_VERSION,
    echoes: 0,
    shards: 0,
    metaUpgrades: {}, // upgradeId -> purchased level
    unlockedCharacters: [], // character ids bought with Shards (see data/characters.js's `locked` flag)
    unlockedPerks: [], // perk ids bought with Shards (see data/shardUnlocks.js)
    ownedPets: {}, // petId -> copy level (1-6), won from the shop's Summon gacha (see data/pets.js), cloud-synced
    equippedPets: [], // petId[], length <= maxPetSlots — only these grant a run bonus (see data/pets.js's getPetBonuses), cloud-synced
    essence: {}, // rarity id -> essence count, earned from maxed-out duplicate pets and fused upward
    // Cosmetics (see data/cosmetics.js) — the third currency (Glimmer) and
    // the vanity loadout it buys. Purely visual, never touches combat stats.
    glimmer: 0, // earned per run (Currency.calcGlimmer), spent in the Wardrobe (ShopCosmeticMenu), cloud-synced
    ownedCosmetics: [], // cosmetic ids bought with Glimmer or auto-unlocked from stat milestones, cloud-synced
    equippedCosmetics: { skin: null, trail: null, fx: null, hat: null }, // category -> equipped cosmetic id (or null = default look), cloud-synced
    settings: {
      muted: false, sfxVolume: 1, musicVolume: 1, highContrast: false,
      // The two halves of the old `reduceEffects` — see MIGRATIONS[7]. Both
      // default ON, as it did: a first-time player must not meet full-strength
      // strobing before they find the settings menu.
      reduceShake: true,
      reduceFlash: true,
      // Presentational motion only (chest arc, mote bob, reel stagger). Off by
      // default — unlike the two above it mitigates nothing, so defaulting it
      // on would just take polish away from players who never open the menu.
      skipAnimations: false,
      // Enemy-side time multiplier, applied at Game._update's `enemyDt`. The
      // assist knob that actually lowers the bar: slowing the whole simulation
      // preserves the difficulty ratio and only makes the game feel sluggish,
      // whereas slowing enemies alone leaves the player fully responsive.
      // Composes with (rather than overwrites) Zero Hour's own scale.
      enemySpeed: 1,
      // Focus and dash are sustained holds; a hold you must maintain through a
      // whole pattern is a standard motor-accessibility problem. Latches them
      // instead. See InputManager.
      toggleHold: false,
      // Act III's Manifold accessibility assist — see the getter below.
      manifoldAssist: false,
      // Wave-stage shape (BULLET-HEAVEN.md §H2). false = the original
      // 4-waves-of-N, clear-then-next stage; true = one timed stage on a
      // rising spawn curve. Shipped as a toggle rather than a replacement
      // because it is the riskiest change in that plan and the only honest
      // way to judge it is to play both. Defaults ON — the ramp is the
      // intended shape, the toggle exists to turn it back OFF.
      waveRamp: true,
      // Deathbomb (BULLET-HELL.md §D3) — a hit taken on a full ultimate
      // meter hangs for a beat, and firing the ultimate inside that beat
      // refunds it. Defaults ON: it only ever softens outcomes, and it costs
      // a full bar every time. The toggle exists because it changes when
      // damage resolves, which is the kind of thing a player who has learned
      // the old timing should be able to put back.
      deathbomb: true,
      // 'auto' follows hardware detection (see core/deviceMode.js) — 'on'/'off'
      // override it, e.g. a touchscreen laptop that wants the joystick anyway,
      // or a phone with a paired keyboard that doesn't.
      mobileControls: 'auto',
      // Per-run opt-outs on the champion-select screen (Game.js's
      // disableEchoUpgrades/disableShardPerks/disablePetBonuses) — persisted
      // so a choice (e.g. for a fair challenge run) carries over to the next
      // run instead of silently resetting every time.
      disableEchoUpgrades: false,
      disableShardPerks: false,
      disablePetBonuses: false,
    },
    // Whether the scripted first-run tutorial (systems/TutorialDirector.js)
    // has been played or skipped. Device-local, not cloud-synced: it's about
    // whether the hands at THIS keyboard know the controls, not the account.
    tutorialSeen: false,
    profileName: '', // local display name used for global leaderboard submissions; no password
    redeemedCodes: [], // uppercased redeem codes already claimed (see data/redeemCodes.js), cloud-synced
    // Build snapshot of the most recent run (character, weapons+levels,
    // passives) — the Echo Duel's ghost is built from this (data/echoBoss.js).
    // Captured in Game._endRun, cloud-synced. Null until a run has ended.
    lastEchoSnapshot: null,
    statistics: {
      runs: 0,
      wins: 0,
      losses: 0,
      normalWins: 0,
      endlessRuns: 0,
      bossesDefeated: 0,
      highestGauntletIndex: -1,
      highestAscension: 0,
      totalDamageDealt: 0,
      totalSurvivalSeconds: 0,
      fastestClearSeconds: null,
      bestScore: 0,
      totalGrazes: 0,
      totalEchoDuelsWon: 0,
      // spriteKeys of every boss ever defeated (gauntlet, challenge, heralds)
      // — drives the Archive codex's progressive unlock (ui/LoreMenu.js).
      // Device-local like the rest of statistics (not cloud-synced).
      defeatedBosses: [],
      // ids of every act ever CLEARED, in the act's own run (data/acts.js) —
      // gates the next act on the act select screen, and the endings in the
      // Archive. Deliberately not `defeatedBosses`: that records a kill in any
      // mode, including a one-off Boss Challenge against an act's final boss,
      // which is not the same as having played the act. See MIGRATIONS[6].
      clearedActs: [],
      // --- achievement counters (BULLET-HEAVEN.md §H6) ---------------------
      // Every one of these is read through a `?? 0` / `?? []` guard by
      // data/achievements.js rather than through a schema migration, matching
      // the convention totalGrazes already set: a save written before a
      // counter existed simply lacks it, and starts accumulating from 0.
      totalMotesCollected: 0,
      bestMoteBurst: 0, // most motes collected in a single tick — the vacuum record
      bestGrazeChain: 0, // longest unbroken graze chain (BULLET-HELL.md §D5)
      totalBulletsAbsorbed: 0, // matched-tone bullets absorbed (BULLET-HELL.md §D7)
      bestResonance: 0, // longest unbroken resonance chain (BULLET-HELL.md §D7)
      totalFusions: 0,
      fusionsAchieved: [], // fusion def ids ever completed
      bestRunFusions: 0,
      totalPactsTaken: 0,
      bestRunLevel: 0,
      totalChestsOpened: 0,
      bestLateChest: 0, // largest chest AFTER the hardcoded generous opening three
      bossesByCharacter: {}, // character id -> most bosses defeated in one run as them
      // --- spell-card capture records (BULLET-HELL.md §D1) -----------------
      // pattern name -> { attempts, captures }. Written by
      // recordPatternResults at the end of every run, INCLUDING Boss
      // Challenge and practice runs: practising a card you can't capture is
      // exactly what those modes are for, so the record has to count there
      // even though nothing else about them pays out. Device-local, like
      // defeatedBosses.
      patternRecords: {},
      totalCaptures: 0,
    },
    // Achievement ids already unlocked and paid out (see
    // data/achievements.js). Kept outside `statistics` because it is a ledger
    // of granted rewards, not a tally — losing it would double-pay every
    // achievement, so it must never be reset by a statistics migration.
    unlockedAchievements: [],
  };
}

/**
 * Persists permanent progression (currency + meta upgrade levels) to
 * localStorage. Kept as a small abstraction — not because localStorage is
 * complicated, but so a real backend/DB can be swapped in later (see
 * ARCHIVE.md) without touching any code that calls SaveManager.
 *
 * Schema is versioned: `_migrate` steps an old save's version up to
 * `SCHEMA_VERSION` field-by-field rather than discarding it — see below.
 */
export class SaveManager {
  constructor(storage = window.localStorage) {
    this.storage = storage;
    this.data = this._load();
    this._listeners = [];
    // Set by CloudSync after a successful login/session-restore, cleared on
    // logout. In-memory only (not part of `data`/localStorage) since it's
    // derived from the current auth session, re-established fresh on every
    // page load rather than persisted.
    this.accountUsername = null;
  }

  _load() {
    try {
      const raw = this.storage.getItem(STORAGE_KEY);
      if (!raw) return defaultSave();
      const parsed = JSON.parse(raw);
      if (parsed.version !== SCHEMA_VERSION) return this._migrate(parsed);
      // `settings` is merged one level deeper than the rest of the save so an
      // older save missing a newly-added setting (e.g. reduceEffects) still
      // gets that field's default instead of losing it to the shallow spread.
      return { ...defaultSave(), ...parsed, settings: { ...defaultSave().settings, ...parsed.settings } };
    } catch {
      return defaultSave();
    }
  }

  /**
   * Upgrades an old save to the current shape instead of discarding it.
   * `MIGRATIONS[v]` transforms a save from version `v` to `v + 1` (field
   * renames/reshapes only — never delete an entry once it's shipped, so a
   * save several versions behind still steps through each one in order).
   * The field-preserving merge below is what actually matters right
   * now — it keeps everything from `old` that still has a matching field
   * name and only defaults in what's missing (e.g. a setting added after
   * `old` was last saved), rather than nuking progress wholesale.
   */
  _migrate(old) {
    let data = old && typeof old === 'object' ? old : {};
    let fromVersion = typeof data.version === 'number' ? data.version : 0;
    while (fromVersion < SCHEMA_VERSION) {
      const step = MIGRATIONS[fromVersion];
      if (step) data = step(data);
      fromVersion++;
    }
    return {
      ...defaultSave(),
      ...data,
      version: SCHEMA_VERSION,
      settings: { ...defaultSave().settings, ...data.settings },
    };
  }

  save({ silent = false } = {}) {
    this.storage.setItem(STORAGE_KEY, JSON.stringify(this.data));
    // `silent` skips notifying listeners — used by replaceCloudFields/
    // resetToGuest, which apply a change that *came from* CloudSync, so
    // notifying would just push the same data straight back to Supabase.
    if (!silent) this._listeners.forEach((fn) => fn(this.data));
  }

  /** Subscribe to every non-silent save(). Returns an unsubscribe function. */
  onChange(fn) {
    this._listeners.push(fn);
    return () => {
      this._listeners = this._listeners.filter((listener) => listener !== fn);
    };
  }

  /** The subset of save data that syncs to a cloud account — currency, shop upgrades, unlocks, pets, redeemed codes. Settings/profileName stay per-device. */
  getCloudFields() {
    return {
      echoes: this.data.echoes,
      shards: this.data.shards,
      metaUpgrades: this.data.metaUpgrades,
      unlockedCharacters: this.data.unlockedCharacters,
      unlockedPerks: this.data.unlockedPerks,
      ownedPets: this.data.ownedPets,
      equippedPets: this.data.equippedPets,
      essence: this.data.essence,
      glimmer: this.data.glimmer,
      ownedCosmetics: this.data.ownedCosmetics,
      equippedCosmetics: this.data.equippedCosmetics,
      redeemedCodes: this.data.redeemedCodes,
      lastEchoSnapshot: this.data.lastEchoSnapshot,
    };
  }

  /** Echo Duel: stores the just-ended run's build snapshot (see Game._endRun). */
  setLastEchoSnapshot(snapshot) {
    this.data.lastEchoSnapshot = snapshot;
    this.save();
  }

  /**
   * Overwrites the cloud-synced fields from a remote save, e.g. right
   * after login. ownedPets/equippedPets/redeemedCodes fall back to the
   * LOCAL values (not empty) when absent from the remote payload — a cloud
   * row from before those columns existed must not wipe a local collection
   * that now carries run bonuses.
   */
  replaceCloudFields({ echoes, shards, metaUpgrades, unlockedCharacters, unlockedPerks, ownedPets, equippedPets, essence, glimmer, ownedCosmetics, equippedCosmetics, redeemedCodes, lastEchoSnapshot }) {
    this.data.echoes = echoes ?? 0;
    this.data.shards = shards ?? 0;
    this.data.metaUpgrades = metaUpgrades ?? {};
    this.data.unlockedCharacters = unlockedCharacters ?? [];
    this.data.unlockedPerks = unlockedPerks ?? [];
    this.data.ownedPets = ownedPets ?? this.data.ownedPets;
    this.data.equippedPets = equippedPets ?? this.data.equippedPets;
    this.data.essence = essence ?? this.data.essence;
    this.data.glimmer = glimmer ?? 0;
    // Fall back to the LOCAL collection/loadout (not empty) when absent from
    // the remote payload — a cloud row from before the cosmetic columns
    // existed must not wipe a locally-earned wardrobe (same reasoning as
    // ownedPets/equippedPets above).
    this.data.ownedCosmetics = ownedCosmetics ?? this.data.ownedCosmetics;
    this.data.equippedCosmetics = equippedCosmetics ?? this.data.equippedCosmetics;
    this.data.redeemedCodes = redeemedCodes ?? this.data.redeemedCodes;
    this.data.lastEchoSnapshot = lastEchoSnapshot ?? this.data.lastEchoSnapshot;
    this.save({ silent: true });
  }

  /** Resets progress to a fresh guest state on logout, so a shared computer doesn't keep showing the previous account's numbers. Local device preferences (settings, guest display name) are untouched. */
  resetToGuest() {
    const fresh = defaultSave();
    this.data = { ...fresh, profileName: this.data.profileName, settings: this.data.settings };
    this.save({ silent: true });
  }

  addEchoes(amount) {
    this.data.echoes += Math.max(0, Math.floor(amount));
    this.save();
  }

  spendEchoes(amount) {
    if (this.data.echoes < amount) return false;
    this.data.echoes -= amount;
    this.save();
    return true;
  }

  get echoes() {
    return this.data.echoes;
  }

  addShards(amount) {
    this.data.shards += Math.max(0, Math.floor(amount));
    this.save();
  }

  spendShards(amount) {
    if (this.data.shards < amount) return false;
    this.data.shards -= amount;
    this.save();
    return true;
  }

  // Adopt an authoritative Shard balance computed server-side (Reprise's
  // pull_fragments debits Shards inside the RPC, so the client can't spend
  // locally — it must take the server's post-debit number, or its next
  // CloudSync push would clobber the decrement with a stale higher value).
  syncShardsFromServer(amount) {
    if (!Number.isFinite(amount) || amount < 0) return;
    this.data.shards = Math.floor(amount);
    this.save();
  }

  get shards() {
    return this.data.shards;
  }

  addGlimmer(amount) {
    this.data.glimmer += Math.max(0, Math.floor(amount));
    this.save();
  }

  spendGlimmer(amount) {
    if (this.data.glimmer < amount) return false;
    this.data.glimmer -= amount;
    this.save();
    return true;
  }

  get glimmer() {
    return this.data.glimmer;
  }

  // --- Cosmetics (data/cosmetics.js) ---

  ownsCosmetic(id) {
    return this.data.ownedCosmetics.includes(id);
  }

  unlockCosmetic(id) {
    if (!this.data.ownedCosmetics.includes(id)) this.data.ownedCosmetics.push(id);
    this.save();
  }

  /** The equipped cosmetic id for a category ('skin'|'trail'|'fx'|'hat'), or null for the default look. */
  getEquippedCosmetic(category) {
    return this.data.equippedCosmetics?.[category] ?? null;
  }

  /** Equips an owned cosmetic into its category slot (one per category, unlike pets' capped array). Returns false if not owned. */
  equipCosmetic(category, id) {
    if (!this.ownsCosmetic(id)) return false;
    this.data.equippedCosmetics = { ...this.data.equippedCosmetics, [category]: id };
    this.save();
    return true;
  }

  unequipCosmetic(category) {
    this.data.equippedCosmetics = { ...this.data.equippedCosmetics, [category]: null };
    this.save();
  }

  /**
   * A champion is playable if it ships open, or if it has been bought.
   *
   * This used to be `return true` unconditionally, with every champion in
   * data/characters.js carrying `locked: false`. The entire ladder behind it
   * was already built and working — `unlockedCharacters` on the save,
   * `unlockCharacter` below, CharacterSelect's locked card with its Unlock
   * button and affordability check — and none of it ran. Shards had a gacha
   * and a finite perk list to spend on and nothing a player actually *wants*;
   * champions are the most motivating sink the game has, because they change
   * how a run plays rather than adding a percentage to it.
   */
  isCharacterUnlocked(character) {
    if (!character.locked) return true;
    return this.data.unlockedCharacters.includes(character.id);
  }

  unlockCharacter(id) {
    if (!this.data.unlockedCharacters.includes(id)) this.data.unlockedCharacters.push(id);
    this.save();
  }

  isPerkUnlocked(id) {
    // Same "everything unlocked for now" arrangement as isCharacterUnlocked
    // above — see the ALL_PERKS_UNLOCKED note in data/shardUnlocks.js.
    // Purchases still record to unlockedPerks, so flipping the flag back
    // loses nothing.
    if (ALL_PERKS_UNLOCKED) return true;
    return this.data.unlockedPerks.includes(id);
  }

  unlockPerk(id) {
    if (!this.data.unlockedPerks.includes(id)) this.data.unlockedPerks.push(id);
    this.save();
  }

  get ownedPets() {
    return this.data.ownedPets;
  }

  isPetOwned(id) {
    return id in this.data.ownedPets;
  }

  getPetLevel(id) {
    return this.data.ownedPets[id] ?? 0;
  }

  get essence() { return this.data.essence; }

  addEssence(rarity, amount = 1) {
    this.data.essence[rarity] = (this.data.essence[rarity] ?? 0) + Math.max(0, Math.floor(amount));
    this.save();
  }

  /**
   * Records a gacha pull. A first-ever pull of a pet owns it at level 1; a
   * duplicate below MAX_COPY_LEVEL levels it up; a duplicate at
   * MAX_COPY_LEVEL converts to essence instead (the "wasted pull" case,
   * same essence economy as before this pet leveled at all).
   */
  recordPetPull(id, rarity) {
    const level = this.data.ownedPets[id];
    if (level === undefined) {
      this.data.ownedPets[id] = 1;
      this.save();
      return { status: 'new', level: 1 };
    }
    if (level >= MAX_COPY_LEVEL) {
      this.data.essence[rarity] = (this.data.essence[rarity] ?? 0) + 1;
      this.save();
      return { status: 'maxed', essence: 1 };
    }
    this.data.ownedPets[id] = level + 1;
    this.save();
    return { status: 'leveled', level: level + 1 };
  }

  fuseEssence(rarity) {
    const index = ESSENCE_RARITIES.indexOf(rarity);
    if (index < 0 || index === ESSENCE_RARITIES.length - 1 || (this.data.essence[rarity] ?? 0) < ESSENCE_FUSION_COST) return false;
    this.data.essence[rarity] -= ESSENCE_FUSION_COST;
    const next = ESSENCE_RARITIES[index + 1];
    this.data.essence[next] = (this.data.essence[next] ?? 0) + 1;
    this.save();
    return true;
  }

  fuseAllEssence(rarity) {
    const index = ESSENCE_RARITIES.indexOf(rarity);
    const available = this.data.essence[rarity] ?? 0;
    if (index < 0 || index === ESSENCE_RARITIES.length - 1) return 0;
    const conversions = Math.floor(available / ESSENCE_FUSION_COST);
    if (conversions < 1) return 0;
    this.data.essence[rarity] -= conversions * ESSENCE_FUSION_COST;
    const next = ESSENCE_RARITIES[index + 1];
    this.data.essence[next] = (this.data.essence[next] ?? 0) + conversions;
    this.save();
    return conversions;
  }

  /** Essence cost to buy this pet's next copy: ESSENCE_PET_COST for a first-ever copy, +1 for each copy already owned after that. */
  getPetBuyCost(id) {
    return ESSENCE_PET_COST + this.getPetLevel(id);
  }

  /** Buys the pet's next copy with essence — an unowned pet at ESSENCE_PET_COST, or (if already owned and below MAX_COPY_LEVEL) a leveling-up duplicate at an escalating cost (see getPetBuyCost). */
  buyPet(id, rarity) {
    const level = this.getPetLevel(id);
    if (level >= MAX_COPY_LEVEL) return false;
    const cost = this.getPetBuyCost(id);
    if ((this.data.essence[rarity] ?? 0) < cost) return false;
    this.data.essence[rarity] -= cost;
    this.data.ownedPets[id] = level + 1;
    this.save();
    return true;
  }

  /** 1 by default, 2 once the "Bonded Companion" Shard perk is unlocked (see data/shardUnlocks.js). */
  get maxPetSlots() {
    return this.isPerkUnlocked('petSecondSlot') ? 2 : 1;
  }

  get equippedPets() {
    return this.data.equippedPets;
  }

  equipPet(id) {
    if (!this.isPetOwned(id) || this.data.equippedPets.includes(id)) return false;
    if (this.data.equippedPets.length >= this.maxPetSlots) return false;
    this.data.equippedPets.push(id);
    this.save();
    return true;
  }

  unequipPet(id) {
    this.data.equippedPets = this.data.equippedPets.filter((petId) => petId !== id);
    this.save();
  }

  getUpgradeLevel(id) {
    return this.data.metaUpgrades[id] ?? 0;
  }

  /**
   * Has this save bought *any* permanent progression yet — an Echo upgrade, a
   * Shard perk, or an equipped pet?
   *
   * Read by CharacterSelect to decide whether to show the three "Disable Echo
   * Upgrades / Shard Perks / Pet Bonuses" toggles. Those are challenge-run
   * controls: they opt a run out of bonuses the player has earned. On a fresh
   * save there are no such bonuses, so the toggles are three checkboxes that
   * provably do nothing, sitting above the champion roster on the first
   * screen a new player ever reads — and they cost the vertical space the
   * roster's second row needs. They appear the moment they can matter.
   *
   * Deliberately keyed on *owning* the bonus rather than on the disable flags
   * themselves — except that a still-set flag also counts, so a player who
   * disabled something and then respent their way back to zero can always
   * find the switch they flipped.
   */
  get hasPermanentProgression() {
    return Object.values(this.data.metaUpgrades).some((level) => level > 0)
      || this.data.unlockedPerks.length > 0
      || this.data.equippedPets.length > 0
      || this.data.settings.disableEchoUpgrades
      || this.data.settings.disableShardPerks
      || this.data.settings.disablePetBonuses;
  }

  setUpgradeLevel(id, level) {
    this.data.metaUpgrades[id] = level;
    this.save();
  }

  get muted() {
    return this.data.settings.muted;
  }

  setMuted(muted) {
    this.data.settings.muted = muted;
    this.save();
  }

  get sfxVolume() {
    return this.data.settings.sfxVolume;
  }

  setSfxVolume(v) {
    this.data.settings.sfxVolume = v;
    this.save();
  }

  get musicVolume() {
    return this.data.settings.musicVolume;
  }

  setMusicVolume(v) {
    this.data.settings.musicVolume = v;
    this.save();
  }

  /** BULLET-HEAVEN.md §H2 — see the settings default for what the two shapes are. */
  get waveRamp() {
    return this.data.settings.waveRamp;
  }

  setWaveRamp(enabled) {
    this.data.settings.waveRamp = enabled;
    this.save();
  }

  /** BULLET-HELL.md §D3 — see the settings default. `??` covers old saves. */
  get deathbomb() {
    return this.data.settings.deathbomb ?? true;
  }

  setDeathbomb(enabled) {
    this.data.settings.deathbomb = enabled;
    this.save();
  }

  /**
   * Camera shake, hit-stop and slow-mo — the time-feel/vestibular half of the
   * old `reduceEffects`. `??` covers a save that predates the split and
   * somehow skipped MIGRATIONS[7].
   *
   * NOTE for callers: DEATHBOMB.windowReduced is keyed to THIS flag, not to
   * the flash one — the widened window exists to give back the reaction time
   * that damping slow-mo takes away (config/constants.js).
   */
  get reduceShake() {
    return this.data.settings.reduceShake ?? this.data.settings.reduceEffects ?? true;
  }

  setReduceShake(reduce) {
    this.data.settings.reduceShake = reduce;
    this.save();
  }

  /** Full-screen flashes and every blinking/strobing alpha — the photosensitivity half. */
  get reduceFlash() {
    return this.data.settings.reduceFlash ?? this.data.settings.reduceEffects ?? true;
  }

  setReduceFlash(reduce) {
    this.data.settings.reduceFlash = reduce;
    this.save();
  }

  /**
   * Presentational motion: the spoils chest's arc and bob, XP-mote bobbing,
   * the reel's stagger. Deliberately NOT a mitigation setting — it's for
   * players who just want the game to stop making them wait.
   */
  get skipAnimations() {
    return this.data.settings.skipAnimations ?? false;
  }

  setSkipAnimations(skip) {
    this.data.settings.skipAnimations = skip;
    this.save();
  }

  /** Enemy-side time multiplier in [0.5, 1] — see the settings default. */
  get enemySpeed() {
    const v = this.data.settings.enemySpeed;
    return typeof v === 'number' && v > 0 ? Math.min(1, Math.max(0.5, v)) : 1;
  }

  setEnemySpeed(v) {
    this.data.settings.enemySpeed = Math.min(1, Math.max(0.5, v));
    this.save();
  }

  /** Latch focus/dash instead of requiring a sustained hold — see InputManager. */
  get toggleHold() {
    return this.data.settings.toggleHold ?? false;
  }

  setToggleHold(enabled) {
    this.data.settings.toggleHold = enabled;
    this.save();
  }

  get highContrast() {
    return this.data.settings.highContrast;
  }

  setHighContrast(enabled) {
    this.data.settings.highContrast = enabled;
    this.save();
  }

  /**
   * Act III's Manifold assist (ACT3-BIBLE §3, a shipping requirement rather
   * than a stretch): mirrored control is exactly the mechanic that hurts most
   * under motor or spatial-processing difficulty, so the second body can be
   * switched from mirroring the player to following them automatically, at
   * reduced damage. Defaults off — the mirror is the fight — and is read once
   * per boss stage (Game._startAct3Systems).
   */
  get manifoldAssist() {
    return this.data.settings.manifoldAssist ?? false;
  }

  setManifoldAssist(enabled) {
    this.data.settings.manifoldAssist = enabled;
    this.save();
  }

  /** 'auto' | 'on' | 'off' — see core/deviceMode.js's isMobileModeActive. */
  get mobileControls() {
    return this.data.settings.mobileControls;
  }

  setMobileControls(mode) {
    this.data.settings.mobileControls = mode;
    this.save();
  }

  get disableEchoUpgrades() {
    return this.data.settings.disableEchoUpgrades;
  }

  setDisableEchoUpgrades(disabled) {
    this.data.settings.disableEchoUpgrades = disabled;
    this.save();
  }

  get disableShardPerks() {
    return this.data.settings.disableShardPerks;
  }

  setDisableShardPerks(disabled) {
    this.data.settings.disableShardPerks = disabled;
    this.save();
  }

  get disablePetBonuses() {
    return this.data.settings.disablePetBonuses;
  }

  setDisablePetBonuses(disabled) {
    this.data.settings.disablePetBonuses = disabled;
    this.save();
  }

  get profileName() {
    return this.data.profileName;
  }

  setProfileName(name) {
    this.data.profileName = typeof name === 'string' ? name.trim().slice(0, 20) : '';
    this.save();
  }

  /** False only for a genuinely new player on this device — see defaultSave/MIGRATIONS[5]. */
  get tutorialSeen() {
    return this.data.tutorialSeen === true;
  }

  /** Set once the tutorial is played through OR skipped — either way it's answered. */
  markTutorialSeen() {
    if (this.data.tutorialSeen === true) return;
    this.data.tutorialSeen = true;
    this.save();
  }

  hasRedeemedCode(code) {
    return this.data.redeemedCodes.includes(code.toUpperCase());
  }

  markCodeRedeemed(code) {
    const normalized = code.toUpperCase();
    if (!this.data.redeemedCodes.includes(normalized)) this.data.redeemedCodes.push(normalized);
    this.save();
  }

  get statistics() {
    return this.data.statistics;
  }

  /**
   * Records a boss (by spriteKey) as defeated, for the Archive codex's
   * progressive unlock (ui/LoreMenu.js). Deduped; `?? []` guards saves
   * written before the field existed (same nested-field convention as
   * totalGrazes in recordRunStatistics). Called per boss death in Game, so
   * it covers gauntlet, challenge, and herald kills alike.
   */
  recordBossDefeated(spriteKey) {
    if (!spriteKey) return;
    const stats = this.data.statistics;
    stats.defeatedBosses = stats.defeatedBosses ?? [];
    if (!stats.defeatedBosses.includes(spriteKey)) {
      stats.defeatedBosses.push(spriteKey);
      this.save();
    }
  }

  /**
   * Records an act (by id, data/acts.js) as cleared — the gate that opens the
   * next one on the act select screen. Called from Game._endRun on a win, and
   * only past the rewardless early return, so a Boss Challenge kill against
   * an act's final boss never counts as having cleared the act.
   *
   * Deduped and `?? []`-guarded on the same convention as recordBossDefeated.
   */
  recordActCleared(actId) {
    if (!actId) return;
    const stats = this.data.statistics;
    stats.clearedActs = stats.clearedActs ?? [];
    if (!stats.clearedActs.includes(actId)) {
      stats.clearedActs.push(actId);
      this.save();
    }
  }

  /**
   * Merges a run's spell-card results (BULLET-HELL.md §D1) into the lifetime
   * per-pattern record. `results` is CaptureTracker.records:
   * `{ [patternName]: { attempts, captures } }`.
   *
   * Kept separate from recordRunStatistics because it is called from the
   * rewardless modes too (Boss Challenge, practice), which return before the
   * statistics write — see the field comment in defaultData.
   */
  recordPatternResults(results) {
    if (!results) return;
    const names = Object.keys(results);
    if (!names.length) return;
    const stats = this.data.statistics;
    stats.patternRecords = stats.patternRecords ?? {};
    let captured = 0;
    for (const name of names) {
      const { attempts = 0, captures = 0 } = results[name] ?? {};
      const record = stats.patternRecords[name] ?? (stats.patternRecords[name] = { attempts: 0, captures: 0 });
      record.attempts += Math.max(0, Math.floor(attempts));
      record.captures += Math.max(0, Math.floor(captures));
      captured += Math.max(0, Math.floor(captures));
    }
    stats.totalCaptures = (stats.totalCaptures ?? 0) + captured;
    this.save();
  }

  /** Lifetime record for one pattern name, or null if it has never run. */
  patternRecord(name) {
    return this.data.statistics.patternRecords?.[name] ?? null;
  }

  recordRunStatistics({
    won = false,
    endless = false,
    ascensionLevel = 0,
    gauntletIndex = -1,
    bossesDefeated = 0,
    totalDamageDealt = 0,
    survivalTime = 0,
    leaderboardScore = 0,
    grazes = 0,
    bestGrazeChain = 0,
    bulletsAbsorbed = 0,
    bestResonance = 0,
    echoDuelsWon = 0,
    // --- achievement inputs (BULLET-HEAVEN.md §H6) ------------------------
    motesCollected = 0,
    bestMoteBurst = 0,
    fusions = 0,
    fusionIds = [],
    pactsTaken = 0,
    level = 0,
    chestsOpened = 0,
    bestLateChest = 0,
    characterId = null,
  }) {
    const stats = this.data.statistics;
    stats.runs++;
    if (won) stats.wins++;
    else stats.losses++;
    if (won && !endless) stats.normalWins++;
    if (endless) stats.endlessRuns++;
    stats.bossesDefeated += Math.max(0, Math.floor(bossesDefeated));
    stats.highestGauntletIndex = Math.max(stats.highestGauntletIndex, gauntletIndex);
    stats.highestAscension = Math.max(stats.highestAscension, ascensionLevel);
    stats.totalDamageDealt += Math.max(0, totalDamageDealt);
    stats.totalSurvivalSeconds += Math.max(0, survivalTime);
    if (won && !endless && (stats.fastestClearSeconds === null || survivalTime < stats.fastestClearSeconds)) {
      stats.fastestClearSeconds = survivalTime;
    }
    stats.bestScore = Math.max(stats.bestScore, leaderboardScore);
    // ?? 0 rather than a migration: saves written before the graze stat
    // existed simply lack the field.
    stats.totalGrazes = (stats.totalGrazes ?? 0) + Math.max(0, Math.floor(grazes));
    stats.bestGrazeChain = Math.max(stats.bestGrazeChain ?? 0, Math.floor(bestGrazeChain));
    // Polarity absorption (BULLET-HELL.md §D7) — same `?? 0` convention.
    stats.totalBulletsAbsorbed = (stats.totalBulletsAbsorbed ?? 0) + Math.max(0, Math.floor(bulletsAbsorbed));
    stats.bestResonance = Math.max(stats.bestResonance ?? 0, Math.floor(bestResonance));
    stats.totalEchoDuelsWon = (stats.totalEchoDuelsWon ?? 0) + Math.max(0, Math.floor(echoDuelsWon));

    // Achievement counters. Same `?? 0` convention as totalGrazes above.
    stats.totalMotesCollected = (stats.totalMotesCollected ?? 0) + Math.max(0, Math.floor(motesCollected));
    stats.bestMoteBurst = Math.max(stats.bestMoteBurst ?? 0, Math.floor(bestMoteBurst));
    stats.totalFusions = (stats.totalFusions ?? 0) + Math.max(0, Math.floor(fusions));
    stats.bestRunFusions = Math.max(stats.bestRunFusions ?? 0, Math.floor(fusions));
    stats.fusionsAchieved = stats.fusionsAchieved ?? [];
    for (const id of fusionIds) {
      if (!stats.fusionsAchieved.includes(id)) stats.fusionsAchieved.push(id);
    }
    stats.totalPactsTaken = (stats.totalPactsTaken ?? 0) + Math.max(0, Math.floor(pactsTaken));
    stats.bestRunLevel = Math.max(stats.bestRunLevel ?? 0, Math.floor(level));
    stats.totalChestsOpened = (stats.totalChestsOpened ?? 0) + Math.max(0, Math.floor(chestsOpened));
    stats.bestLateChest = Math.max(stats.bestLateChest ?? 0, Math.floor(bestLateChest));
    if (characterId) {
      stats.bossesByCharacter = stats.bossesByCharacter ?? {};
      stats.bossesByCharacter[characterId] = Math.max(
        stats.bossesByCharacter[characterId] ?? 0,
        Math.floor(bossesDefeated),
      );
    }
    this.save();
  }

  /** Achievement ids already unlocked. `?? []` guards saves predating the field. */
  get unlockedAchievements() {
    this.data.unlockedAchievements = this.data.unlockedAchievements ?? [];
    return this.data.unlockedAchievements;
  }

  /**
   * Grants every achievement whose condition now holds and pays out its
   * Echoes, returning only the ones that were newly unlocked.
   *
   * Idempotent by construction: the unlocked ledger is the payment record, so
   * calling this on every run end AND every time the list is opened is safe.
   * That second call matters — it is what lets an achievement added in a later
   * version unlock retroactively from statistics already on disk, instead of
   * requiring the player to re-earn something they've already done.
   *
   * @param {object|null} run - the just-finished run, or null when
   *   re-evaluating outside one (run-scoped achievements simply can't fire).
   */
  evaluateAchievements(run = null) {
    const unlocked = this.unlockedAchievements;
    const owned = new Set(unlocked);
    const newly = satisfiedAchievements({ stats: this.data.statistics, run })
      .filter((achievement) => !owned.has(achievement.id));
    if (newly.length === 0) return [];

    let payout = 0;
    for (const achievement of newly) {
      unlocked.push(achievement.id);
      payout += achievement.echoes ?? 0;
    }
    // Written through the same field addEchoes uses rather than calling it, so
    // the ledger and the payout land in ONE save() — a crash between them
    // would otherwise either double-pay or silently swallow the reward.
    this.data.echoes += payout;
    this.save();
    return newly;
  }
}
